import { z } from "zod";
import { validateCompany, validateName, validatePackstationAddress, validatePackstationNumber, validatePhone, validatePhoneDE, validatePostalCode, validateStreetAndNumber } from "../../../../utils/fieldsValidator";

// Type for customer address creation from DB schema
export type CustomerAddressCreation = {
  id?: number;
  title?: string;
  address_type: string;
  address_sub_type?: string;
  first_name: string;
  middle_name: string;
  last_name: string;
  gender: number;
  address: string;
  house_number?: string;
  street?: string;
  packstation_address?: string;
  packstation_number?: string;
  company?: string;
  zip: string;
  city: string;
  country_id: string;
  phone: string;
  is_default: boolean;
  created_at?: string;
  updated_at?: string;
};

// Create schema for customer address creation
export const createCustomerAddressCreationSchema = (language: string, isPackstation = false) => z.object({
  title: z.string().optional(),
  address_type: z.string().default(isPackstation ? 'packstation' : 'regular'),
  address_sub_type: z.string().optional(),
  first_name: z.string().superRefine((val, ctx) => {
    const { isValid, errorMsg } = validateName(val, language);
    if (!isValid) ctx.addIssue({ code: z.ZodIssueCode.custom, message: errorMsg });
  }),
  last_name: z.string().superRefine((val, ctx) => {
    const { isValid, errorMsg } = validateName(val, language);
    if (!isValid) ctx.addIssue({ code: z.ZodIssueCode.custom, message: errorMsg });
  }),
  middle_name: z.string().optional(),
  address: z.string().optional(),
  house_number: z.string().optional(),
  street: z.string().optional(),
  packstation_address: z.string().optional(),
  packstation_number: z.string().optional(),
  company: z.string().optional().superRefine((val, ctx) => {
    if (!val) return;
    const { isValid, errorMsg } = validateCompany(val, language);
    if (!isValid) ctx.addIssue({ code: z.ZodIssueCode.custom, message: errorMsg });
  }),
  zip: z.string(),
  city: z.string().superRefine((val, ctx) => {
    const { isValid, errorMsg } = validateName(val, language);
    if (!isValid) ctx.addIssue({ code: z.ZodIssueCode.custom, message: errorMsg });
  }),
  country_id: z.string(),
  phone: z.string(),
  is_default: z.boolean({ coerce: true }).default(false),
}).superRefine((data, ctx) => {
  // Validate phone based on selected country
  const phoneResult = data.country_id?.toLowerCase() === 'de'
    ? validatePhoneDE(data.phone, language)
    : validatePhone(data.phone, language);
  if (!phoneResult.isValid) {
    ctx.addIssue({
      path: ['phone'],
      code: z.ZodIssueCode.custom,
      message: phoneResult.errorMsg
    });
  }

  // Validate zip based on selected country
  const { isValid, errorMsg } = validatePostalCode(data.zip, language, data.country_id.toLowerCase());
  if (!isValid) {
    ctx.addIssue({
      path: ['zip'],
      code: z.ZodIssueCode.custom,
      message: errorMsg
    });
  }

  // If address type is packstation, packstation fields are required
  if (data.address_type === 'packstation') {
    // Validate the packstation_address format
    let { isValid, errorMsg } = validatePackstationAddress(data.packstation_address!, language);
    if (!isValid) {
      ctx.addIssue({
        path: ['packstation_address'],
        code: z.ZodIssueCode.custom,
        message: errorMsg
      });
    }
    // Validate the packstation_number format
    ({ isValid, errorMsg } = validatePackstationNumber(data.packstation_number!, language));
    if (!isValid) {
      ctx.addIssue({
        path: ['packstation_number'],
        code: z.ZodIssueCode.custom,
        message: errorMsg
      });
    }
  } else { //Regular address
    // Validate the format
    const { isValid, errorMsg } = validateStreetAndNumber(data.address!, language);
    if (!isValid) {
      ctx.addIssue({
        path: ['address'],
        code: z.ZodIssueCode.custom,
        message: errorMsg
      });
    }
  }
});


// Default values for customer address creation
export const defaultCustomerAddressCreationValues: CustomerAddressCreation = {
  title: "",
  address_type: "regular",
  address_sub_type: "apartment",
  first_name: "",
  middle_name: "",
  last_name: "",
  gender: 0,
  address: "",
  zip: "",
  city: "",
  country_id: "DE",
  phone: "",
  is_default: false
};

